import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Skeleton } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import YovoImage from '../common/YovoImage';
import {
  DESTINATION_CARD_DESCRIPTION_FALLBACK,
  FEATURED_DESTINATION_ICON_FONT_SIZE,
  TRENDING_SPOTLIGHT_CITY_FALLBACK,
  TRENDING_SPOTLIGHT_COUNTRY_FALLBACK,
  TRENDING_SPOTLIGHT_CROSSFADE_MS,
  TRENDING_SPOTLIGHT_EXPLORE_PREFIX,
  TRENDING_SPOTLIGHT_IMAGE_LIMIT,
  TRENDING_SPOTLIGHT_MORE_IMAGES_LABEL,
  TRENDING_SPOTLIGHT_ROTATE_MS,
  TRENDING_SPOTLIGHT_THUMBNAIL_COUNT,
} from './homepageCarousel.constants';
import styles from './TrendingSpotlightSection.module.css';

function padIndex(value) {
  return String(value).padStart(2, '0');
}

function pushImages(images, list) {
  if (!Array.isArray(list)) return;
  list.forEach((entry) => {
    if (entry?.image) images.push(entry.image);
  });
}

export function collectDestinationImages(item) {
  const destination = item?.output_json;
  if (!destination) return [];
  const images = [];
  if (destination.hero?.image) images.push(destination.hero.image);
  pushImages(images, destination.attractions?.sites);
  pushImages(images, destination.history?.historicalSites);
  pushImages(images, destination.accommodation?.areas);
  pushImages(images, destination.foodAndDrinks?.items);
  pushImages(images, destination.transportation?.options);
  pushImages(images, destination.bestTimeToVisit?.seasons);
  pushImages(images, destination.nextDestinations);
  return images.slice(0, TRENDING_SPOTLIGHT_IMAGE_LIMIT);
}

function tripView(item) {
  return {
    city: item?.output_json?.hero?.title || item?.name || TRENDING_SPOTLIGHT_CITY_FALLBACK,
    country: item?.output_json?.place?.country || TRENDING_SPOTLIGHT_COUNTRY_FALLBACK,
    description: item?.cardDescription || item?.output_json?.hero?.description || DESTINATION_CARD_DESCRIPTION_FALLBACK,
  };
}

/**
 * One-city spotlight. The parent owns the homepage fetch and passes the trips in.
 */
function TrendingSpotlightSection({
  sectionId,
  title,
  description,
  items,
  loading,
  onExplore,
  icon: Icon = TrendingUpIcon,
  iconColor,
}) {
  const trips = Array.isArray(items) ? items : [];
  const [destinationIndex, setDestinationIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);
  const [previousImageUrl, setPreviousImageUrl] = useState('');
  const fadeTimer = useRef(null);

  const safeIndex = Math.min(destinationIndex, Math.max(0, trips.length - 1));
  const trip = trips[safeIndex];
  const images = useMemo(() => collectDestinationImages(trip), [trip]);
  const shownIndex = images.length === 0 ? 0 : Math.min(imageIndex, images.length - 1);
  const currentImage = images[shownIndex] || '';
  const view = tripView(trip);
  const thumbnails = images.slice(0, TRENDING_SPOTLIGHT_THUMBNAIL_COUNT);

  useEffect(() => {
    setDestinationIndex(0);
    setImageIndex(0);
    setPreviousImageUrl('');
  }, [items]);

  useEffect(() => () => {
    if (fadeTimer.current) clearTimeout(fadeTimer.current);
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') return undefined;
    if (images.length <= 1) return undefined;
    const interval = setInterval(() => {
      setImageIndex((prev) => {
        const shown = images[prev] || images[0] || '';
        setPreviousImageUrl(shown);
        if (fadeTimer.current) clearTimeout(fadeTimer.current);
        fadeTimer.current = setTimeout(() => setPreviousImageUrl(''), TRENDING_SPOTLIGHT_CROSSFADE_MS);
        return (prev + 1) % images.length;
      });
    }, TRENDING_SPOTLIGHT_ROTATE_MS);
    return () => clearInterval(interval);
  }, [images]);

  const rememberPrevious = (url) => {
    if (process.env.NODE_ENV === 'test') return;
    setPreviousImageUrl(url || '');
    if (fadeTimer.current) clearTimeout(fadeTimer.current);
    fadeTimer.current = setTimeout(() => setPreviousImageUrl(''), TRENDING_SPOTLIGHT_CROSSFADE_MS);
  };

  const goToDestination = (next) => {
    setDestinationIndex(next);
    setImageIndex(0);
    setPreviousImageUrl('');
  };

  const goToImage = (next) => {
    if (next === shownIndex || next < 0 || next >= images.length) return;
    rememberPrevious(currentImage);
    setImageIndex(next);
  };

  const openTrip = () => {
    if (onExplore && trip) onExplore(trip);
  };

  if (loading || trips.length === 0) {
    return (
      <section
        id={sectionId}
        className={styles.section}
        data-testid="trending-spotlight-shimmer"
        aria-busy="true"
      >
        <div className={styles.panel}>
          <div className={styles.shimmerCopy}>
            <Skeleton variant="rounded" width="48%" height={36} />
            <Skeleton variant="rounded" width="70%" height={16} />
          </div>
          <div className={styles.stage}>
            <Skeleton variant="rounded" width="100%" height={420} sx={{ maxWidth: 460, borderRadius: '12px' }} />
            <div className={styles.details}>
              <Skeleton variant="rounded" width="100%" height={32} />
              <Skeleton variant="rounded" width="100%" height={48} />
              <Skeleton variant="rounded" width="36%" height={18} />
              <div className={styles.thumbs}>
                {[0, 1, 2, 3].map((slot) => (
                  <Skeleton key={slot} variant="rounded" height={120} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const total = trips.length;
  const startLabel = padIndex(safeIndex + 1);
  const totalLabel = padIndex(total);
  const progress = ((safeIndex + 1) / total) * 100;

  return (
    <section id={sectionId} className={styles.section}>
      <div className={styles.panel}>
        <div className={styles.header}>
          <div className={styles.copy}>
            <div className={styles.titleRow}>
              <Icon
                className={styles.titleIcon}
                data-testid="trending-spotlight-icon"
                aria-hidden="true"
                fontSize={FEATURED_DESTINATION_ICON_FONT_SIZE}
                style={iconColor ? { color: iconColor } : undefined}
              />
              <h2 className={styles.title}>{title}</h2>
            </div>
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>
          <div className={styles.controls}>
            <div className={styles.counter} aria-live="polite">
              <span>{startLabel}</span>
              <span className={styles.counterTrack} aria-hidden="true">
                <span className={styles.counterFill} style={{ width: `${progress}%` }} />
              </span>
              <span>{totalLabel}</span>
            </div>
            <button
              type="button"
              className={styles.arrow}
              aria-label="Previous destinations"
              disabled={safeIndex <= 0}
              onClick={() => goToDestination(safeIndex - 1)}
            >
              ‹
            </button>
            <button
              type="button"
              className={styles.arrow}
              aria-label="Next destinations"
              disabled={safeIndex >= total - 1}
              onClick={() => goToDestination(safeIndex + 1)}
            >
              ›
            </button>
          </div>
        </div>

        {trip ? (
          <div className={styles.stage}>
            <div
              className={styles.hero}
              data-testid="trending-spotlight-hero"
              onClick={openTrip}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  openTrip();
                }
              }}
              role="link"
              tabIndex={0}
            >
              {previousImageUrl ? (
                <YovoImage
                  src={previousImageUrl}
                  alt=""
                  width={460}
                  height={447}
                  className={`${styles.heroPhoto} ${styles.heroPhotoPrevious}`}
                />
              ) : null}
              <YovoImage
                src={currentImage}
                alt={view.city}
                width={460}
                height={447}
                className={`${styles.heroPhoto} ${styles.heroPhotoCurrent}`}
              />
              <div className={styles.heroCopy}>
                <p className={styles.heroTitle}>{view.city}</p>
                <p className={styles.heroCountry}>{view.country}</p>
              </div>
              <div className={styles.heroNav}>
                <button
                  type="button"
                  className={styles.arrow}
                  aria-label="Previous image"
                  disabled={shownIndex <= 0}
                  onClick={(event) => {
                    event.stopPropagation();
                    goToImage(shownIndex - 1);
                  }}
                >
                  ‹
                </button>
                <button
                  type="button"
                  className={styles.arrow}
                  aria-label="Next image"
                  disabled={shownIndex >= images.length - 1}
                  onClick={(event) => {
                    event.stopPropagation();
                    goToImage(shownIndex + 1);
                  }}
                >
                  ›
                </button>
              </div>
            </div>

            <div className={styles.details}>
              <div className={styles.cityRow} data-testid="trending-spotlight-city-row">
                <h3 className={styles.city}>{view.city}</h3>
                <button type="button" className={styles.explore} onClick={openTrip}>
                  {TRENDING_SPOTLIGHT_EXPLORE_PREFIX} {view.city}
                  <ArrowForwardIcon sx={{ fontSize: '16px' }} />
                </button>
              </div>
              <p className={styles.cityDescription}>{view.description}</p>
              <p className={styles.moreLabel}>{TRENDING_SPOTLIGHT_MORE_IMAGES_LABEL}</p>
              <div className={styles.thumbs}>
                {thumbnails.map((image, index) => (
                  <button
                    key={`${image}-${index}`}
                    type="button"
                    className={`${styles.thumb} ${index === shownIndex ? styles.thumbActive : ''}`}
                    aria-label={`Show image ${index + 1} of ${view.city}`}
                    onClick={() => goToImage(index)}
                  >
                    <YovoImage
                      className={styles.thumbPhoto}
                      src={image}
                      alt={view.city}
                      width={320}
                      height={220}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default TrendingSpotlightSection;
